import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Box, CardMedia, Typography } from "@mui/material";
import { formatWebM, formatWebOptimized } from "./helpers";

export interface TranscriptVideoPreviewHandle {
    seekTo: (seconds: number) => void;
    pause: () => void;
    play: () => void;
}

interface Props {
    video: any;
    onTimeUpdate?: (seconds: number) => void;
}

export const TranscriptVideoPreview = forwardRef<TranscriptVideoPreviewHandle, Props>(
    function TranscriptVideoPreview({ video, onTimeUpdate }, ref) {
        const videoRef = useRef<HTMLVideoElement | null>(null);

        const sources = useMemo(() => {
            const list: { url: string; type: string }[] = [];
            const webm = formatWebM(video)?.url;
            if (webm) list.push({ url: webm, type: "video/webm" });
            const opt = formatWebOptimized(video)?.url;
            if (opt) list.push({ url: opt, type: "video/mp4" });
            const raw = video?.location || video?.reviewS3File?.location;
            if (raw) list.push({ url: raw, type: "video/mp4" });
            return list;
        }, [video]);

        useImperativeHandle(ref, () => ({
            seekTo: (seconds: number) => {
                const el = videoRef.current;
                if (!el || Number.isNaN(seconds)) return;
                el.currentTime = seconds;
                el.play().catch(() => {});
            },
            pause: () => videoRef.current?.pause(),
            play: () => {
                videoRef.current?.play().catch(() => {});
            },
        }));

        if (sources.length === 0) {
            return (
                <Box
                    sx={{
                        width: "100%",
                        aspectRatio: "16 / 9",
                        bgcolor: "#1C1A17",
                        color: "#999",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "8px",
                    }}
                >
                    <Typography variant="body2">No video preview available</Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ width: "100%", bgcolor: "#000", borderRadius: "8px", overflow: "hidden" }}>
                <CardMedia
                    component="video"
                    ref={videoRef as any}
                    controls
                    preload="metadata"
                    style={{ width: "100%", height: "auto", display: "block" }}
                    onTimeUpdate={(e: any) =>
                        onTimeUpdate?.((e.currentTarget as HTMLVideoElement).currentTime)
                    }
                >
                    {sources.map((s, i) => (
                        <source key={i} src={s.url} type={s.type} />
                    ))}
                </CardMedia>
            </Box>
        );
    },
);
